/**
 * How the sender answers each slot type while composing.
 *
 * The sender-facing twin of `SlotViews`: where a view is what the AUTHOR sees
 * of a slot in the builder, a control is what the SENDER uses to answer it in
 * the composer. A slot's `type` tag is a DISPATCH KEY here as well — each type
 * declares its control once, as one `SlotTypeControl` object, and the
 * `SLOT_CONTROLS` registry maps the tag to it. `SlotControl` is the one shell,
 * so the composer renders a question without ever asking what kind it is.
 *
 *   select    the options it offers as chips, plus its write-in when it has one,
 *             whose placeholder hints at what to write
 *   fillIn    a text field, with a counter when the slot sets a limit
 *   optional  an include switch, labelled with what it would include; a
 *             recipient's own detail in the label is a chip that picks whose
 *   text      nothing: its text is not the sender's to answer
 *   goal      nothing, for the same reason
 *
 * Every control reads the slot through `SlotLogic`, never through its own idea
 * of what an answer means. What a control offers is
 * `SlotLogic.possibleValues(slot, addressingMode)`, and what each offered value
 * reads as on screen is what it would print into the message —
 * `SlotLogic.evaluate(SlotLogic.withAnswer(slot, value), env)` — so a chip for
 * `{_profileName}` reads as the sender's name, and a label can never drift
 * from what is actually sent.
 *
 * Adding a type means adding one entry here: the registry's `satisfies` clause
 * covers every tag in the union, so the compiler names the type that has no
 * control rather than leaving the composer silently skipping its question.
 */

import React, { useState } from 'react';
import { ContactLogic, RecipientChip, type Contact } from '@/modules/contact';
import type { AddressingMode, PreviewSegment } from '@/modules/share';
import { PreviewLogic } from '@/modules/share/preview_logic';
import { PRIMITIVES, type Environment } from './lang';
import { type FillInSlot, type OptionalSlot, type SelectSlot } from './schema';
import { SlotLogic, type SlotOfType, type SlotType } from './slot_logic';
import type { EvaluatedForm } from './compose_logic';
import type { AnsweredSlot, SharingFormSlot } from './types';
import styles from './SlotControls.module.css';

/**
 * Who a message goes to, and what its slots resolve against for any one of
 * them. In a direct text each recipient gets a message of their own, so a
 * value that is theirs — their name — is only an example of what the slot
 * prints.
 */
export interface SlotRecipients {
  readonly contacts: readonly Contact[];
  /**
   * The form evaluated for the message to one recipient, with that
   * recipient's own details marked (`PreviewLogic.markPerRecipient`).
   */
  markedFormFor(recipient: Contact): EvaluatedForm;
}

/** What every control is handed: the slot as answered so far, and where to write. */
export interface SlotControlProps<S extends SharingFormSlot = SharingFormSlot> {
  /** The slot, carrying the sender's current answer. */
  slot: AnsweredSlot<S>;
  /** The environment the slot prints in for this message (`EvaluatedForm.slotEnvironments`). */
  env: Environment;
  /** How many people the message reaches, which decides what is offered. */
  addressingMode: AddressingMode;
  /** Who the message goes to. */
  recipients: SlotRecipients;
  testId: string;
  disabled?: boolean;
  /**
   * Whether this is the slot the topic takes the sender's allowed detail
   * from. Its control says so under the prompt, and that it may be left blank.
   */
  isAllowedDetail?: boolean;
  /** Store a new answer for this slot; `''` clears it. */
  onAnswer: (answer: string) => void;
  /**
   * The words last left in a select's write-in, kept by the caller so they
   * outlive this control: the composer remounts its questions, and reopening
   * the write-in should still bring the words back. Without it the control
   * remembers them only while it stays mounted.
   */
  lastWriteIn?: string;
  /** Keep the write-in's words as the sender leaves it for an option or ✕. */
  onLeaveWriteIn?: (words: string) => void;
}

/**
 * One slot type's control, over slots already narrowed to that type.
 *
 * `Control` is declared as a METHOD for the reason `SlotTypeView`'s members
 * are: TypeScript checks method parameters bivariantly, which is what lets the
 * registry hand a `SlotTypeControl<SelectSlot>` back through `control()` as a
 * `SlotTypeControl<SharingFormSlot>`. The shell passes back the very slot it
 * looked the control up with, so each one only ever sees its own type.
 */
export interface SlotTypeControl<S extends SharingFormSlot = SharingFormSlot> {
  readonly type: S['type'];
  Control(props: SlotControlProps<S>): React.ReactElement | null;
}

/** What one offered value reads as on screen: what it would print. */
function printValue(
  slot: AnsweredSlot,
  value: string,
  env: Environment
): string {
  return SlotLogic.evaluate(SlotLogic.withAnswer(slot, value), env).trim();
}

/** Text and goal slots take no answer, so they have nothing to put on screen. */
function NoControl(): null {
  return null;
}

/** Where a slot evaluates in one recipient's marked form. */
function labelEnvironment(
  marked: EvaluatedForm,
  slot: SharingFormSlot
): Environment {
  return marked.slotEnvironments.get(slot.id) ?? marked.resultingEnvironment;
}

/** A field's heading, and the allowed detail note when it is that field. */
const ControlLabel: React.FC<{
  label: string;
  htmlFor?: string;
  isAllowedDetail?: boolean;
  testId: string;
}> = ({ label, htmlFor, isAllowedDetail = false, testId }) => (
  <>
    <label className={styles.label} htmlFor={htmlFor}>
      {label}
    </label>
    {isAllowedDetail && (
      <p
        className={styles.allowedDetailNote}
        data-testid={`${testId}-allowed-detail`}
      >
        This is a special “allowed detail” field, which can appear in later
        reshares by friends of friends. May be left blank.
      </p>
    )}
  </>
);

/**
 * A select answers with one of its options or, when it offers one, the
 * sender's own words in its write-in.
 *
 * An option that prints nothing for this message — `{_profileName}` for a
 * sender with no profile name — gets no chip: picking it would read as an
 * answer while sending nothing, which is also why `SlotLogic.isAnswered`
 * refuses it.
 *
 * Tapping the chosen chip again clears it. Opening the write-in clears the pick
 * too, and closing it with ✕ restores the option it replaced, so the sender can
 * look at the write-in without losing what they had. Leaving the write-in for
 * an option keeps its words in the same way: reopening it brings them back.
 */
function SelectControl({
  slot,
  env,
  addressingMode,
  testId,
  disabled,
  isAllowedDetail,
  onAnswer,
  lastWriteIn: keptWriteIn,
  onLeaveWriteIn,
}: SlotControlProps<SelectSlot>): React.ReactElement {
  const writeIn = SlotLogic.writeInOf(slot);
  const offered = SlotLogic.possibleValues(slot, addressingMode)
    .map(value => ({ value, label: printValue(slot, value, env) }))
    .filter(option => option.label !== '');
  // Matched against every declared value, not just the offered ones, so an
  // answer this mode happens not to offer is never mistaken for the sender's
  // own words and poured into the write-in.
  const isDeclared = SlotLogic.possibleValues(slot).includes(slot.answer);
  const picked = isDeclared ? slot.answer : null;

  // Words of the sender's own keep the write-in open, including ones restored
  // after this mounted; otherwise it is open while the sender has it open.
  const [writeInOpened, setWriteInOpened] = useState(false);
  const writingIn =
    writeIn !== null && (writeInOpened || (!isDeclared && slot.answer !== ''));
  const [pickBeforeWriteIn, setPickBeforeWriteIn] = useState<string | null>(
    picked
  );
  // The sender's words as they last left the write-in, so reopening it brings
  // them back instead of an empty field. The caller's copy wins when it keeps
  // one.
  const [ownLastWriteIn, setOwnLastWriteIn] = useState(
    isDeclared ? '' : slot.answer
  );
  const lastWriteIn = keptWriteIn ?? ownLastWriteIn;
  const inputId = `${testId}-write-in`;

  const leaveWriteIn = () => {
    if (writingIn) {
      setOwnLastWriteIn(slot.answer);
      onLeaveWriteIn?.(slot.answer);
    }
    setWriteInOpened(false);
  };

  const openWriteIn = () => {
    setPickBeforeWriteIn(picked);
    setWriteInOpened(true);
    onAnswer(lastWriteIn);
  };

  const closeWriteIn = () => {
    leaveWriteIn();
    onAnswer(pickBeforeWriteIn ?? '');
  };

  return (
    <div className={styles.control} data-testid={testId}>
      <ControlLabel
        label={slot.prompt}
        isAllowedDetail={isAllowedDetail}
        testId={testId}
      />
      <div className={styles.chips}>
        {offered.map(({ value, label }, i) => {
          const selected = !writingIn && value === picked;
          return (
            <button
              key={value}
              type="button"
              className={styles.chip}
              data-selected={selected}
              aria-pressed={selected}
              disabled={disabled}
              data-testid={`${testId}-option-${i}`}
              onClick={() => {
                leaveWriteIn();
                onAnswer(selected ? '' : value);
              }}
            >
              {label}
            </button>
          );
        })}
        {writeIn && (
          <button
            type="button"
            className={`${styles.chip} ${styles.writeInChip}`}
            data-selected={writingIn}
            aria-pressed={writingIn}
            disabled={disabled}
            data-testid={`${testId}-other`}
            onClick={writingIn ? closeWriteIn : openWriteIn}
          >
            {writeIn.label ?? 'Other'}
          </button>
        )}
      </div>
      {writeIn && writingIn && (
        <div className={styles.writeInRow}>
          <input
            id={inputId}
            className={styles.textInput}
            value={slot.answer}
            maxLength={writeIn.maxlength}
            placeholder={writeIn.placeholder ?? 'Write your own'}
            aria-label={`${slot.prompt}: your own words`}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="done"
            disabled={disabled}
            data-testid={inputId}
            onChange={e => onAnswer(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
          />
          <button
            type="button"
            className={styles.closeWriteIn}
            aria-label="Close your own words"
            disabled={disabled}
            data-testid={`${inputId}-close`}
            onClick={closeWriteIn}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * A fill-in is answered in the sender's own words, whatever they type. A limit
 * the author set shows as a running count beside the field.
 */
function FillInControl({
  slot,
  testId,
  disabled,
  isAllowedDetail,
  onAnswer,
}: SlotControlProps<FillInSlot>): React.ReactElement {
  const inputId = `${testId}-input`;
  return (
    <div className={styles.control} data-testid={testId}>
      <ControlLabel
        label={slot.prompt}
        htmlFor={inputId}
        isAllowedDetail={isAllowedDetail}
        testId={testId}
      />
      <div className={styles.fillInBox}>
        <textarea
          id={inputId}
          className={styles.fillInInput}
          value={slot.answer}
          rows={2}
          maxLength={slot.maxlength}
          placeholder={slot.placeholder}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          disabled={disabled}
          data-testid={inputId}
          onChange={e => onAnswer(e.target.value)}
        />
        {slot.maxlength !== undefined && (
          <span className={styles.counter} data-testid={`${testId}-counter`}>
            {slot.answer.length} / {slot.maxlength}
          </span>
        )}
      </div>
    </div>
  );
}

function plain(text: string): PreviewSegment {
  return { text, perRecipient: false };
}

/**
 * What an optional's switch says it would include, in pieces, so a
 * recipient's own detail within it can render as a chip.
 *
 * `env` carries per-recipient values marked (`PreviewLogic.markPerRecipient`)
 * when they differ from one message to the next, and the pieces keep those
 * marks. When the text is nothing but one such value — `' {_recipientInformalName}'`
 * — the label is that value alone: "Include [Percy]". Anything more, whether
 * words of the text's own or a primitive every message shares, is shown in
 * quotes exactly as it would print: "Include “ my contact, [Percy],”", "Include
 * “ 24 September 2026”".
 */
function includeLabel(
  slot: AnsweredSlot<OptionalSlot>,
  included: string,
  env: Environment
): readonly PreviewSegment[] {
  const pieces = PreviewLogic.segments(
    SlotLogic.evaluate(SlotLogic.withAnswer(slot, included), env)
  );
  const visible = pieces.filter(
    piece => piece.perRecipient || piece.text.trim() !== ''
  );
  if (visible.length === 1 && visible[0].perRecipient) {
    return [plain('Include '), visible[0]];
  }
  return [plain('Include “'), ...pieces, plain('”')];
}

/**
 * An optional is answered by leaving its text out or putting it in, so its
 * control is a switch, labelled with what it would include.
 *
 * In a direct text to more than one person, a recipient's own detail in the
 * label is an example — the first recipient's until the sender picks someone
 * else — and renders as a chip that offers the others.
 */
function OptionalControl({
  slot,
  env,
  addressingMode,
  recipients,
  testId,
  disabled,
  onAnswer,
}: SlotControlProps<OptionalSlot>): React.ReactElement | null {
  const [recipientId, setRecipientId] = useState<string | null>(null);
  const included = SlotLogic.possibleValues(slot, addressingMode).find(
    value => value !== ''
  );
  if (included === undefined) return null;
  const isOn = slot.answer === included;
  const labelId = `${testId}-label`;

  const { contacts } = recipients;
  const variesByRecipient = addressingMode === 'dm' && contacts.length > 1;
  const recipient =
    contacts.find(contact => contact.localId === recipientId) ?? contacts[0];
  const labelEnv = variesByRecipient
    ? labelEnvironment(recipients.markedFormFor(recipient), slot)
    : env;

  return (
    <div
      className={`${styles.control} ${styles.optionalRow}`}
      data-testid={testId}
    >
      <span id={labelId} className={styles.optionalLabel} data-testid={labelId}>
        {includeLabel(slot, included, labelEnv).map((piece, i) =>
          piece.perRecipient ? (
            <RecipientChip
              key={i}
              text={piece.text}
              recipients={contacts}
              onPick={contact => setRecipientId(contact.localId)}
              pickerHeader={`Show ${PRIMITIVES[piece.primitive].doc} for`}
              ariaLabel={`Example for ${ContactLogic.displayName(recipient)}. Show it for someone else`}
              disabled={disabled}
              testId={`${testId}-recipient-chip`}
            />
          ) : (
            <React.Fragment key={i}>{piece.text}</React.Fragment>
          )
        )}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={isOn}
        aria-labelledby={labelId}
        className={styles.switch}
        data-on={isOn}
        disabled={disabled}
        data-testid={`${testId}-switch`}
        onClick={() => onAnswer(isOn ? '' : included)}
      >
        <span className={styles.switchKnob} />
      </button>
    </div>
  );
}

const TEXT: SlotTypeControl<SlotOfType<'text'>> = {
  type: 'text',
  Control: NoControl,
};

const GOAL: SlotTypeControl<SlotOfType<'goal'>> = {
  type: 'goal',
  Control: NoControl,
};

const OPTIONAL: SlotTypeControl<OptionalSlot> = {
  type: 'optional',
  Control: OptionalControl,
};

const SELECT: SlotTypeControl<SelectSlot> = {
  type: 'select',
  Control: SelectControl,
};

const FILL_IN: SlotTypeControl<FillInSlot> = {
  type: 'fillIn',
  Control: FillInControl,
};

/**
 * Every slot type's control, by tag. `satisfies` is what makes this
 * exhaustive: a new member of the slot union with no entry here is a compile
 * error naming exactly the type that is missing.
 */
export const SLOT_CONTROLS = {
  text: TEXT,
  goal: GOAL,
  optional: OPTIONAL,
  select: SELECT,
  fillIn: FILL_IN,
} as const satisfies {
  readonly [T in SlotType]: SlotTypeControl<SlotOfType<T>>;
};

/**
 * The control for a slot. It hands back the registry entry itself, for the
 * reason `SlotViews`' lookup does: an entry bound to the slot would be built
 * per call, giving `Control` a new identity every render, and the field being
 * typed into would remount on each keystroke.
 */
function control(slot: SharingFormSlot): SlotTypeControl {
  return SLOT_CONTROLS[slot.type];
}

/** The sender's control for one slot, rendered by its own type. */
export const SlotControl: React.FC<SlotControlProps> = props => {
  const { Control } = control(props.slot);
  return <Control {...props} />;
};
